import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { 
  Image as ImageIcon, 
  Camera, 
  Microphone,
  X,
  Check,
  PaperPlaneRight,
  FileText,
  MapPin,
  User,
  PencilSimple,
  FolderOpen,
  Plus,
  Sticker
} from '@phosphor-icons/react';
import axios from 'axios';
import CameraCapture from './CameraCapture';
import VoiceRecorder from './VoiceRecorder';
import ImageEditor from './ImageEditor';
import { optimizeImageForUpload } from '../utils/imageUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MediaPicker = ({ onMediaSelected, onClose, userRole = 'customer', onOpenSavedTexts, onOpenSavedPhotos, onOpenStickers }) => {
  const fileInputRef = useRef(null);
  const documentInputRef = useRef(null);
  const addMoreInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');

  // Multi-media editor state
  const [mediaItems, setMediaItems] = useState(null); // [{file, caption, preview, isVideo, editedBlob}]
  const [activeIndex, setActiveIndex] = useState(0);
  const [editingIndex, setEditingIndex] = useState(null); // Index of item being edited in ImageEditor

  // Shared group caption (for multiple items)
  const [groupCaption, setGroupCaption] = useState('');

  // Select files → always go to multi-media editor
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files).slice(0, 10);
    if (!files.length) return;
    
    const items = files.map(file => ({
      file,
      caption: '',
      preview: URL.createObjectURL(file),
      isVideo: file.type.startsWith('video/'),
      editedBlob: null
    }));
    
    setMediaItems(items);
    setActiveIndex(0);
    setGroupCaption('');
  };

  // Add more files (from + button)
  const handleAddMore = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length || !mediaItems) return;
    
    const remaining = 10 - mediaItems.length;
    if (remaining <= 0) {
      toast.error('Maximum 10 files allowed');
      return;
    }
    
    const newItems = files.slice(0, remaining).map(file => ({
      file,
      caption: '',
      preview: URL.createObjectURL(file),
      isVideo: file.type.startsWith('video/'),
      editedBlob: null
    }));
    
    setMediaItems(prev => [...prev, ...newItems]);
    // Switch to first new item
    setActiveIndex(mediaItems.length);
  };

  // When ImageEditor saves an edited image
  const handleItemEditSave = (editedBlob, captionText) => {
    const idx = editingIndex;
    const newPreview = URL.createObjectURL(editedBlob);
    
    setMediaItems(prev => prev.map((item, i) => {
      if (i === idx) {
        // Revoke old preview if it was an edited blob preview
        if (item.editedBlob) URL.revokeObjectURL(item.preview);
        return {
          ...item,
          editedBlob,
          preview: newPreview,
          caption: captionText || item.caption
        };
      }
      return item;
    }));
    setEditingIndex(null);
  };

  // Send all media items
  const handleSendAll = async () => {
    if (!mediaItems?.length) return;
    setIsUploading(true);
    try {
      const isMultiple = mediaItems.length > 1;
      for (let i = 0; i < mediaItems.length; i++) {
        const item = mediaItems[i];
        const formData = new FormData();
        const originalFile = item.editedBlob
          ? new File([item.editedBlob], item.file.name || 'edited.jpg', { type: item.editedBlob.type || 'image/jpeg' })
          : item.file;
        // Normalize images for every chat role. This prevents admin/handler
        // mobile uploads from depending on the device's HEIC support.
        const fileToUpload = !item.isVideo
          ? await optimizeImageForUpload(originalFile)
          : originalFile;
        formData.append('file', fileToUpload);
        
        // For multiple photos: caption only on LAST photo (WhatsApp style)
        // For single photo: use individual caption
        const caption = isMultiple 
          ? (i === mediaItems.length - 1 ? groupCaption : '') 
          : item.caption;
        
        if (item.isVideo) {
          formData.append('media_type', 'video');
          const response = await axios.post(`${API}/upload/media`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          const uploadedFile = response.data?.file || response.data;
          if (!uploadedFile?.url) {
            throw new Error('Upload completed but no media URL was returned');
          }
          onMediaSelected({ type: 'video', url: uploadedFile?.url, caption });
        } else {
          formData.append('media_type', 'image');
          const response = await axios.post(`${API}/upload/media`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          const uploadedFile = response.data?.file || response.data;
          if (!uploadedFile?.url) {
            throw new Error('Upload completed but no media URL was returned');
          }
          onMediaSelected({ type: 'image', url: uploadedFile?.url, caption, file: fileToUpload });
        }
      }
      // Cleanup previews
      mediaItems.forEach(item => URL.revokeObjectURL(item.preview));
      setMediaItems(null);
      setActiveIndex(0);
      setGroupCaption('');
      onClose();
    } catch (err) {
      const reason = err.response?.data?.detail || err.response?.data?.error || err.message;
      toast.error(reason ? `Media upload failed: ${reason}` : 'Failed to upload media');
    }
    setIsUploading(false);
  };

  // Remove item from list
  const removeItem = (idx) => {
    if (!mediaItems) return;
    URL.revokeObjectURL(mediaItems[idx].preview);
    const newItems = mediaItems.filter((_, i) => i !== idx);
    if (newItems.length === 0) {
      setMediaItems(null);
      setActiveIndex(0);
      return;
    }
    setMediaItems(newItems);
    if (activeIndex >= newItems.length) {
      setActiveIndex(newItems.length - 1);
    } else if (activeIndex > idx) {
      setActiveIndex(prev => prev - 1);
    }
  };

  // Close multi-media editor
  const closeMediaEditor = () => {
    if (mediaItems) {
      mediaItems.forEach(item => URL.revokeObjectURL(item.preview));
    }
    setMediaItems(null);
    setActiveIndex(0);
    setEditingIndex(null);
  };

  // Handle document/file selection
  const handleDocumentSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload/document`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      onMediaSelected({
        type: 'document',
        url: response.data.url,
        fileName: response.data.original_name || file.name,
        fileSize: response.data.size || file.size,
        extension: response.data.extension
      });
      onClose();
    } catch (error) {
      console.error('Document upload failed:', error);
      toast.error('Upload failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsUploading(false);
    }
  };

  // Camera photo → add to media items
  const handleCameraPhoto = (media) => {
    const file = new File([media.blob], 'camera_photo.jpg', { type: 'image/jpeg' });
    const item = {
      file,
      caption: '',
      preview: URL.createObjectURL(file),
      isVideo: false,
      editedBlob: null
    };
    setMediaItems([item]);
    setActiveIndex(0);
    setShowCamera(false);
  };

  // ===== RENDER SECTIONS =====

  // 1) If editing a specific image in ImageEditor
  if (editingIndex !== null && mediaItems?.[editingIndex]) {
    const item = mediaItems[editingIndex];
    const fileForEditor = item.editedBlob 
      ? new File([item.editedBlob], 'edit.jpg', { type: 'image/jpeg' })
      : item.file;
    
    return (
      <ImageEditor
        imageFile={fileForEditor}
        onSave={handleItemEditSave}
        onCancel={() => setEditingIndex(null)}
      />
    );
  }

  // 2) Multi-media editor (WhatsApp style)
  if (mediaItems?.length) {
    const activeItem = mediaItems[activeIndex] || mediaItems[0];
    
    return (
      <div className="fixed inset-0 bg-black z-50 flex flex-col">
        {/* Header */}
        <div className="bg-[#1f2c34] text-white px-3 py-2.5 flex items-center justify-between flex-shrink-0">
          <button 
            onClick={closeMediaEditor} 
            className="p-1.5 hover:bg-white/10 rounded-full"
            data-testid="media-editor-close"
          >
            <X size={24} weight="bold" />
          </button>
          <span className="text-sm font-medium opacity-70">
            {activeIndex + 1} / {mediaItems.length}
          </span>
          {!activeItem.isVideo && (
            <button 
              onClick={() => setEditingIndex(activeIndex)} 
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#00a884] rounded-full text-sm font-semibold"
              data-testid="media-edit-btn"
            >
              <PencilSimple size={16} weight="bold" />
              Edit
            </button>
          )}
          {activeItem.isVideo && <div className="w-16" />}
        </div>
        
        {/* Main preview area */}
        <div className="flex-1 flex items-center justify-center p-2 overflow-hidden min-h-0">
          {activeItem.isVideo ? (
            <video 
              src={activeItem.preview} 
              className="max-w-full max-h-full object-contain rounded-lg" 
              controls 
              preload="metadata"
            />
          ) : (
            <img 
              src={activeItem.preview} 
              alt="" 
              className="max-w-full max-h-full object-contain rounded-lg" 
            />
          )}
        </div>
        
        {/* Bottom section */}
        <div className="bg-[#1f2c34] flex-shrink-0">
          {/* Thumbnail strip */}
          <div className="flex items-center gap-2 px-3 py-2 overflow-x-auto" data-testid="media-thumbnail-strip">
            {mediaItems.map((item, idx) => (
              <button
                key={idx}
                onClick={() => setActiveIndex(idx)}
                className={`relative w-14 h-14 flex-shrink-0 rounded-lg overflow-hidden border-2 transition-all ${
                  idx === activeIndex ? 'border-[#00a884] scale-105' : 'border-transparent opacity-60'
                }`}
                data-testid={`media-thumb-${idx}`}
              >
                {item.isVideo ? (
                  <video src={item.preview} className="w-full h-full object-cover" preload="metadata" />
                ) : (
                  <img src={item.preview} alt="" className="w-full h-full object-cover" />
                )}
                {/* Remove button */}
                {mediaItems.length > 1 && (
                  <div
                    onClick={(e) => { e.stopPropagation(); removeItem(idx); }}
                    className="absolute top-0 right-0 w-5 h-5 bg-black/80 rounded-bl-lg flex items-center justify-center"
                  >
                    <X size={10} className="text-white" weight="bold" />
                  </div>
                )}
                {/* Edited indicator */}
                {item.editedBlob && (
                  <div className="absolute bottom-0 left-0 right-0 bg-[#00a884]/80 text-[8px] text-white text-center py-0.5">
                    Edited
                  </div>
                )}
              </button>
            ))}
            
            {/* Add more (+) button */}
            {mediaItems.length < 10 && (
              <button
                onClick={() => addMoreInputRef.current?.click()}
                className="w-14 h-14 flex-shrink-0 rounded-lg border-2 border-dashed border-gray-500 flex items-center justify-center hover:border-[#00a884] transition-colors"
                data-testid="media-add-more-btn"
              >
                <Plus size={22} className="text-wa-text-secondary" />
              </button>
            )}
          </div>
          
          {/* Caption + Send */}
          <div className="px-3 pb-3 flex items-center gap-3">
            <div className="flex-1 bg-[#2a3942] rounded-full px-4 py-2">
              {mediaItems.length > 1 ? (
                <input
                  type="text"
                  value={groupCaption}
                  onChange={(e) => setGroupCaption(e.target.value)}
                  placeholder="Add a caption for all photos..."
                  className="w-full bg-transparent text-white placeholder-gray-400 text-sm focus:outline-none"
                  data-testid="media-group-caption-input"
                />
              ) : (
                <input
                  type="text"
                  value={activeItem.caption}
                  onChange={(e) => {
                    const idx = activeIndex;
                    setMediaItems(prev => prev.map((f, i) => i === idx ? { ...f, caption: e.target.value } : f));
                  }}
                  placeholder="Add a caption..."
                  className="w-full bg-transparent text-white placeholder-gray-400 text-sm focus:outline-none"
                  data-testid="media-caption-input"
                />
              )}
            </div>
            <button
              onClick={handleSendAll}
              disabled={isUploading}
              className="w-12 h-12 bg-[#00a884] rounded-full flex items-center justify-center flex-shrink-0 shadow-lg disabled:opacity-50"
              data-testid="media-send-btn"
            >
              {isUploading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <PaperPlaneRight size={24} className="text-white" weight="fill" />
              )}
            </button>
          </div>
        </div>
        
        {/* Hidden input for add more */}
        <input
          ref={addMoreInputRef}
          type="file"
          accept="image/*,video/*"
          multiple
          onChange={handleAddMore}
          className="hidden"
        />
      </div>
    );
  }

  // 3) Camera Capture
  if (showCamera) {
    return (
      <CameraCapture
        onPhotoTaken={handleCameraPhoto}
        onClose={() => setShowCamera(false)}
      />
    );
  }

  // 4) Voice Recorder
  if (showVoiceRecorder) {
    return (
      <VoiceRecorder
        onVoiceSent={(media) => {
          onMediaSelected(media);
          setShowVoiceRecorder(false);
        }}
        onClose={() => setShowVoiceRecorder(false)}
      />
    );
  }

  // 5) Contact Form
  if (showContactForm) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-wa-panel rounded-lg w-full max-w-sm">
          <div className="bg-[#008069] text-white px-4 py-3 rounded-t-lg flex items-center gap-2">
            <User size={20} weight="fill" />
            <span className="font-semibold">Share Contact</span>
          </div>
          <div className="p-4 space-y-3">
            <input
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              placeholder="Contact Name"
              className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:border-[#008069]"
              autoFocus
              data-testid="contact-name-input"
            />
            <input
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              placeholder="Phone Number"
              type="tel"
              className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:border-[#008069]"
              data-testid="contact-phone-input"
            />
            <div className="flex gap-2 pt-2">
              <button onClick={() => { setShowContactForm(false); setContactName(''); setContactPhone(''); }} className="flex-1 py-2 border rounded-lg text-sm hover:bg-wa-hover">Cancel</button>
              <button 
                onClick={() => {
                  if (!contactName.trim() || !contactPhone.trim()) { toast.error('Enter name and phone'); return; }
                  onMediaSelected({ type: 'text', text: `👤 ${contactName.trim()}\n📞 ${contactPhone.trim()}` });
                  setShowContactForm(false);
                  setContactName('');
                  setContactPhone('');
                  onClose();
                }}
                className="flex-1 py-2 bg-[#008069] text-white rounded-lg text-sm hover:bg-[#006e5a]"
                data-testid="send-contact-btn"
              >Send</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 6) Main attachment grid (Bottom sheet)
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div 
        className="fixed inset-0 bg-black/40 z-50 flex items-end justify-center"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div className="bg-wa-panel w-full max-w-md rounded-t-2xl pb-6 pt-3 animate-slideUp" onClick={(e) => e.stopPropagation()}>
          {/* Handle bar */}
          <div className="flex justify-center mb-4">
            <div className="w-10 h-1 bg-gray-300 rounded-full" />
          </div>

          <div className="px-6 grid grid-cols-3 gap-y-6 gap-x-4">
            {(userRole === 'handler' || userRole === 'admin') && onOpenStickers && (
              <button
                onClick={() => { onClose(); onOpenStickers(); }}
                className="flex flex-col items-center gap-2"
                data-testid="attach-stickers"
              >
                <div className="w-14 h-14 rounded-full bg-[#009688] flex items-center justify-center shadow-md">
                  <Sticker size={26} className="text-white" weight="fill" />
                </div>
                <span className="text-xs text-wa-text-primary font-medium">Stickers</span>
              </button>
            )}
            {/* Document */}
            <button
              onClick={() => documentInputRef.current?.click()}
              disabled={isUploading}
              className="flex flex-col items-center gap-2"
              data-testid="attach-document"
            >
              <div className="w-14 h-14 rounded-full bg-[#7f66ff] flex items-center justify-center shadow-md">
                <FileText size={26} className="text-white" weight="fill" />
              </div>
              <span className="text-xs text-wa-text-primary font-medium">Document</span>
            </button>

            {/* Camera */}
            <button
              onClick={() => setShowCamera(true)}
              className="flex flex-col items-center gap-2"
              data-testid="attach-camera"
            >
              <div className="w-14 h-14 rounded-full bg-[#ff2e74] flex items-center justify-center shadow-md">
                <Camera size={26} className="text-white" weight="fill" />
              </div>
              <span className="text-xs text-wa-text-primary font-medium">Camera</span>
            </button>

            {/* Gallery */}
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex flex-col items-center gap-2"
              data-testid="attach-gallery"
            >
              <div className="w-14 h-14 rounded-full bg-[#bf59cf] flex items-center justify-center shadow-md">
                <ImageIcon size={26} className="text-white" weight="fill" />
              </div>
              <span className="text-xs text-wa-text-primary font-medium">Gallery</span>
            </button>

            {/* Audio */}
            <button
              onClick={() => setShowVoiceRecorder(true)}
              className="flex flex-col items-center gap-2"
              data-testid="attach-audio"
            >
              <div className="w-14 h-14 rounded-full bg-[#ee7b30] flex items-center justify-center shadow-md">
                <Microphone size={26} className="text-white" weight="fill" />
              </div>
              <span className="text-xs text-wa-text-primary font-medium">Audio</span>
            </button>

            {/* Location */}
            <button
              onClick={async () => {
                try {
                  if (!navigator.geolocation) {
                    toast.error('Location not supported');
                    return;
                  }
                  navigator.geolocation.getCurrentPosition(
                    (pos) => {
                      const { latitude, longitude } = pos.coords;
                      const mapUrl = `https://maps.google.com/maps?q=${latitude},${longitude}`;
                      onMediaSelected({ type: 'text', text: mapUrl });
                      onClose();
                    },
                    () => { toast.error('Location access denied'); }
                  );
                } catch (e) { toast.error('Failed to get location'); }
              }}
              className="flex flex-col items-center gap-2"
              data-testid="attach-location"
            >
              <div className="w-14 h-14 rounded-full bg-[#1fa855] flex items-center justify-center shadow-md">
                <MapPin size={26} className="text-white" weight="fill" />
              </div>
              <span className="text-xs text-wa-text-primary font-medium">Location</span>
            </button>

            {/* Contact */}
            <button
              onClick={async () => {
                if ('contacts' in navigator && 'ContactsManager' in window) {
                  try {
                    const contacts = await navigator.contacts.select(['name', 'tel'], { multiple: false });
                    if (contacts?.length) {
                      const c = contacts[0];
                      const name = c.name?.[0] || 'Unknown';
                      const phone = c.tel?.[0] || '';
                      onMediaSelected({ type: 'text', text: `👤 ${name}\n📞 ${phone}` });
                      onClose();
                    }
                  } catch(e) {
                    setShowContactForm(true);
                  }
                } else {
                  setShowContactForm(true);
                }
              }}
              className="flex flex-col items-center gap-2"
              data-testid="attach-contact"
            >
              <div className="w-14 h-14 rounded-full bg-[#009de2] flex items-center justify-center shadow-md">
                <User size={26} className="text-white" weight="fill" />
              </div>
              <span className="text-xs text-wa-text-primary font-medium">Contact</span>
            </button>

            {/* Saved Texts - Handler/Admin only */}
            {(userRole === 'handler' || userRole === 'admin') && (
              <button
                onClick={() => {
                  onClose();
                  if (onOpenSavedTexts) {
                    setTimeout(() => onOpenSavedTexts(), 100);
                  }
                }}
                className="flex flex-col items-center gap-2"
                data-testid="attach-saved-texts"
              >
                <div className="w-14 h-14 rounded-full bg-[#5856d6] flex items-center justify-center shadow-md">
                  <FileText size={26} className="text-white" weight="fill" />
                </div>
                <span className="text-xs text-wa-text-primary font-medium">Saved Texts</span>
              </button>
            )}

            {/* Saved Photos - Handler/Admin only */}
            {(userRole === 'handler' || userRole === 'admin') && (
              <button
                onClick={() => {
                  onClose();
                  if (onOpenSavedPhotos) {
                    setTimeout(() => onOpenSavedPhotos(), 100);
                  }
                }}
                className="flex flex-col items-center gap-2"
                data-testid="attach-saved-photos"
              >
                <div className="w-14 h-14 rounded-full bg-[#ff9500] flex items-center justify-center shadow-md">
                  <FolderOpen size={26} className="text-white" weight="fill" />
                </div>
                <span className="text-xs text-wa-text-primary font-medium">Saved Photos</span>
              </button>
            )}
          </div>

          {/* Hidden file inputs */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*"
            multiple
            onChange={handleImageSelect}
            className="hidden"
          />
          <input
            ref={documentInputRef}
            type="file"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar"
            onChange={handleDocumentSelect}
            className="hidden"
          />

          {isUploading && (
            <div className="flex justify-center mt-4">
              <div className="w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>
        <style>{`
          @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
          .animate-slideUp { animation: slideUp 0.25s ease-out; }
        `}</style>
      </div>
    </div>
  );
};

export default MediaPicker;
